import { defineComponent, inject, provide, nextTick } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import { h } from "vue";
import CatErrors from "@/components/catErrors/catErrors.vue";
import CatHelp from "@/components/utilities/help.vue";
import CatLabel from "@/components/utilities/cat-label.vue";
import CatHint from "@/components/utilities/hint.vue";
import heightUtils from "@/components/utilities/heightUtilsMixin.ts";
import CatHelpTrigger from "@/components/catPageTemplate/catHelpTrigger";

export default defineComponent({
  setup(props) {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);

    const showOptionals =
      props.mandatory ||
      (displayParams ? displayParams.showOptionalOnGroupFields : false);
    provide("showOptionals", showOptionals);

    return {
      resolveTxt,
      displayParams
    };
  },
  name: "CatNFields",
  components: {
    CatErrors,
    CatHelp,
    CatLabel,
    CatHelpTrigger,
    CatHint
  },
  mixins: [heightUtils],
  props: {
    id: {
      type: [String, Number],
      description: "Field group ID."
    },
    label: {
      type: [String],
      description: "Field group label."
    },
    hint: {
      type: [String],
      description: "Field group hint text."
    },
    mandatory: {
      type: [Boolean],
      description: "Indicates if field group is mandatory."
    },
    legendMandatory: {
      type: Boolean,
      default: false,
      description:
        "Specifies if the mandatory indicator should show in the legend. By default not displayed on the legend."
    },
    errors: {
      type: [Array],
      description: "List of validation errors."
    },
    readonly: {
      type: [Boolean],
      description: "Indicates if the field group is readonly."
    },
    classes: {
      type: [Array, Boolean],
      description: "List of classes."
    },
    inputHolderClass: {
      type: [String, Boolean],
      default: "input-holder grid-x flex-align-bottom pn",
      description:
        "A class applied to a parent wrapper container of a cat-input."
    },
    margin: {
      type: [Boolean, String],
      description: "Margin value that will be applied between the fields."
    },
    align: {
      type: [Boolean],
      default: true,
      description:
        "Aligns the fields to the bottom, uses .spacer class to fill the space so the fields are aligned evenly when labels, hints or errors have different heights"
    },
    size: {
      type: [String],
      description: "Field size."
    },
    help: {
      type: [Object],
      description: "Field group help text."
    }
  },
  computed: {
    ih_class: function () {
      var css = {};
      if (this.inputHolderClass) {
        css[this.inputHolderClass] = true;
      }
      if (this.size) {
        css[this.size] = true;
      }
      return css;
    },
    fs_class: function () {
      var css = {
        fieldset: true,
        "cat-n-fields": true,
        "custom-classes": !!this.classes,
        invalid:
          this.errors &&
          this.errors.length > 0 &&
          this.errors.some(function (r) {
            return r.level <= 1;
          }),
        optional: !this.mandatory,
        mandatory: this.mandatory,
        "readonly-list": this.readonly,
        separate: this.margin,
        nolabel: !this.label
      };
      if (this.size) {
        css[this.size] = true;
      }
      css.warning =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 2 && !css.invalid;
        });
      css.infomessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 3 && !css.invalid;
        });
      css.debugmessage =
        this.errors &&
        this.errors.length > 0 &&
        this.errors.some(function (r) {
          return r.level == 4;
        });

      return css;
    },
    wrapperClasses: function () {
      let result = [];
      let _this = this;
      // var validTags = _this.$slots.default().filter(function (element) {
      //   return _this.isValidNode(element);
      // });

      _this.getValidNodes().forEach(function (n, idx) {
        result.push(
          _this.classes && _this.classes[idx]
            ? "joined-fields-wrapper " + _this.classes[idx]
            : "joined-fields-wrapper"
        );
      });

      return result;
    },
    displaySettings: function () {
      return this.displayParams
        ? this.displayParams
        : { showOptionalOnGroupFields: false };
    }
  },

  methods: {
    isValidNode: function (element) {
      //valid node is any except html comments and loose text nodes
      return (
        element &&
        element.type.description !== "v-cmt" &&
        element.type.description !== "v-txt"
      );
    },
    //
    getValidNodes: function () {
      let _t = this;

      //get all nodes in the slot and eliminate html comments and loose text
      let validNodes = _t.$slots.default().filter(function (element) {
        return _t.isValidNode(element);
      });
      //if there is only 1 remaining child of type "v-fgt" - it might be a <template...> - get its children
      if (
        validNodes.length === 1 &&
        validNodes[0].type.description === "v-fgt"
      ) {
        return validNodes[0].children;
      }
      //otherwise return the nodes
      return validNodes;
    }
  },

  updated: function () {
    var t = this;
    nextTick(function () {
      if (t.align) {
        t.sizeSpacers(
          t.$refs.wrapper,
          ".fieldset",
          ".cat-error-wrapper",
          ".spacer"
        );
      }
    });
  },

  mounted: function () {
    var t = this;
    nextTick(function () {
      if (t.align) {
        t.sizeSpacers(
          t.$refs.wrapper,
          ".joined-fields-wrapper",
          ".cat-error-wrapper",
          ".spacer"
        );
      }
    });
  },

  render: function () {
    var _this = this;
    const labelProps = {
      fieldset: true,
      label: _this.label,
      labelId: _this.id + "_lbl",
      optional: !_this.mandatory,
      displayRequired: _this.legendMandatory || !_this.mandatory,
      inline: _this.help && !!_this.help.url,
      errors: _this.errors,
      readonly: _this.readonly
    };

    var kidz = [h(CatLabel, labelProps)];

    if (_this.help && _this.help.url) {
      kidz.push(
        h(CatHelpTrigger, {
          url: _this.help.url,
          title: _this.label,
          sourceId: _this.id
        })
      );
      kidz.push(h("div", { class: "clearfix" }));
    }
    kidz.push(h(CatHint, { text: _this.hint }));
    if (_this.help && _this.help.text) {
      kidz.push(
        h(CatHelp, {
          class: { mbt: true },
          heading: _this.help.title,
          text: _this.help.text,
          id: _this.id,
          ariaDescribedby: this.label && this.id ? _this.id + "_lbl" : null
        })
      );
    }

    var ihKidz = [];
    var lvl = 0;

    _this.getValidNodes().forEach(function (vnode, idx) {
      const element = vnode;
      //console.log("**************** element", element);
      if (_this.isValidNode(element)) {
        ihKidz.push(
          h(
            "div",
            {
              class: _this.wrapperClasses[lvl]
            },
            [
              element,
              h("div", {
                class: "spacer",
                role: "presentation"
              })
            ]
          )
        );
        lvl = lvl + 1;
      }
    });

    kidz.push(h("div", { class: _this.ih_class }, ihKidz));

    kidz.push(h(CatErrors, { errors: _this.errors }));
    return h("fieldset", { class: _this.fs_class, ref: "wrapper" }, kidz);
  }
});
